/**
 * Tool Declarations for Gemini 2.5 Pro Function Calling
 * 
 * Converts CHAT_TOOLS definitions into Gemini 2.5 Pro function declarations
 * that can be registered with the model for native function calling.
 * These declarations are used exclusively with Gemini 2.5 Pro in the Farm Manager.
 */

// Import TOOL_IDS constants - these match the tool IDs used in chatToolsService
const TOOL_IDS = {
        ACCOUNT_OVERVIEW: "account-overview",
        BILLING_STATUS: "billing-status",
        NOTIFICATION_PREFERENCES: "notification-preferences",
        TREATMENT_HISTORY: "treatment-history",
        BIRTH_HISTORY: "birth-history",
        EVENT_HISTORY: "event-history",
        WORK_PLANNER_OVERVIEW: "work-planner-overview",
        WORK_PLANNER_WORKERS: "work-planner-workers",
        WORK_PLANNER_CREATE_TASK: "work-planner-create-task",
        BIONODE_STATUS: "bionode-status",
        BIONODE_ANALYSIS: "bionode-analysis",
        ANIMAL_DETAILS: "animal-details",
        NAVIGATE_PAGE: "navigate-page",
        NAVIGATE_ANIMAL: "navigate-animal",
        FARM_KPI_OVERVIEW: "farm-kpi-overview",
        FARM_KPI_TRENDS: "farm-kpi-trends",
        SEARCH_KNOWLEDGE_BASE: "search-knowledge-base",
        PRODUCTION_HISTORY: "production-history",
        // Calf-specific tools
        CALF_LIFECYCLE_OVERVIEW: "calf-lifecycle-overview",
        VACCINATION_STATUS: "vaccination-status",
        WEIGHT_GROWTH_ANALYSIS: "weight-growth-analysis",
        DISEASE_OVERVIEW: "disease-overview",
        CALF_HEALTH_KPIS: "calf-health-kpis",
        // Diagnostic and analysis tools
        HEALTH_DIAGNOSIS_ASSIST: "health-diagnosis-assist",
        COMPARATIVE_ANALYSIS: "comparative-analysis",
        GENERATE_REPORT: "generate-report",
        ACTIVITY_ANOMALY_ANALYSIS: "activity-anomaly-analysis",
        FEEDING_ANALYSIS: "feeding-analysis",
};

/**
 * Builds Gemini 2.5 Pro function declarations for all chat tools
 * @returns {Array} Array of function declaration objects compatible with Gemini 2.5 Pro
 */
export function buildToolDeclarations() {
        return [
                {
                        name: TOOL_IDS.PRODUCTION_HISTORY,
                        description: "Retrieves specific production records for a calf including weaning, movements, and general lifecycle events.",
                        parameters: {
                                type: "object",
                                properties: {
                                        recordType: {
                                                type: "string",
                                                enum: [
                                                        "weaning",
                                                        "movement",
                                                ],
                                                description: "Type of production record to retrieve",
                                        },
                                        animalId: {
                                                type: "string",
                                                description: "Animal ID or MAC address",
                                        },
                                        limit: {
                                                type: "number",
                                                description: "Number of records to return (default: 20)",
                                                minimum: 1,
                                                maximum: 50,
                                        },
                                },
                                required: ["recordType", "animalId"],
                        },
                },
                {
                        name: TOOL_IDS.SEARCH_KNOWLEDGE_BASE,
                        description: "Searches the farm management knowledge base for how-to guides, best practices, health protocols, and feature documentation. Use this when the user asks 'how do I...', 'what is...', or about farm management procedures.",
                        parameters: {
                                type: "object",
                                properties: {
                                        query: {
                                                type: "string",
                                                description: "Search query keywords or question",
                                        },
                                },
                                required: ["query"],
                        },
                },
                {
                        name: TOOL_IDS.FARM_KPI_OVERVIEW,
                        description: "Retrieves the most recent KPI (Key Performance Indicator) metrics documents from the user's farm data, providing comprehensive calf health performance summaries including mortality rate, ADG, vaccination compliance, and disease incidence.",
                        parameters: {
                                type: "object",
                                properties: {
                                        date: {
                                                type: "string",
                                                description: "Specific date in YYYY-MM-DD format to retrieve metrics for",
                                        },
                                        startDate: {
                                                type: "string",
                                                description: "Start date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        endDate: {
                                                type: "string",
                                                description: "End date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        periodType: {
                                                type: "string",
                                                enum: ["daily", "weekly", "monthly", "yearly"],
                                                description: "Filter by period type",
                                        },
                                        limit: {
                                                type: "number",
                                                description: "Number of records to return (1-10, default: 5)",
                                                minimum: 1,
                                                maximum: 10,
                                        },
                                },
                        },
                },
                {
                        name: TOOL_IDS.FARM_KPI_TRENDS,
                        description: "Queries time-series data points for a single KPI metric to analyze trends over time. Use this to track changes in specific metrics like mortality rate, ADG, or vaccination compliance.",
                        parameters: {
                                type: "object",
                                properties: {
                                        kpi: {
                                                type: "string",
                                                description: "KPI metric name to query (e.g., 'mortalityRate', 'avgDailyGain', 'brdRate', 'vaccinationCompliance')",
                                        },
                                        startDate: {
                                                type: "string",
                                                description: "Start date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        endDate: {
                                                type: "string",
                                                description: "End date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        periodType: {
                                                type: "string",
                                                enum: ["daily", "weekly", "monthly", "yearly"],
                                                description: "Filter by period type",
                                        },
                                        limit: {
                                                type: "number",
                                                description: "Number of data points to return (2-24, default: 6)",
                                                minimum: 2,
                                                maximum: 24,
                                        },
                                },
                                required: ["kpi"],
                        },
                },
                {
                        name: TOOL_IDS.ACCOUNT_OVERVIEW,
                        description: "Retrieves user account information including profile details, contact information, and system settings.",
                        parameters: {
                                type: "object",
                                properties: {},
                        },
                },
                {
                        name: TOOL_IDS.TREATMENT_HISTORY,
                        description: "Retrieves treatment history for animals. Can filter by animal ID, date range, or treatment status.",
                        parameters: {
                                type: "object",
                                properties: {
                                        animalId: {
                                                type: "string",
                                                description: "Animal ID or MAC address to filter treatments",
                                        },
                                        startDate: {
                                                type: "string",
                                                description: "Start date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        endDate: {
                                                type: "string",
                                                description: "End date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        limit: {
                                                type: "number",
                                                description: "Number of records to return (1-50, default: 10)",
                                                minimum: 1,
                                                maximum: 50,
                                        },
                                },
                        },
                },
                {
                        name: TOOL_IDS.BIRTH_HISTORY,
                        description: "Retrieves calf birth/arrival records and lifecycle stage transitions. Can filter by animal ID or date range.",
                        parameters: {
                                type: "object",
                                properties: {
                                        animalId: {
                                                type: "string",
                                                description: "Animal ID or MAC address to filter records",
                                        },
                                        startDate: {
                                                type: "string",
                                                description: "Start date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        endDate: {
                                                type: "string",
                                                description: "End date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        limit: {
                                                type: "number",
                                                description: "Number of records to return (1-50, default: 10)",
                                                minimum: 1,
                                                maximum: 50,
                                        },
                                },
                        },
                },
                {
                        name: TOOL_IDS.EVENT_HISTORY,
                        description: "Retrieves general event history. Can filter by animal ID, event type, or date range.",
                        parameters: {
                                type: "object",
                                properties: {
                                        animalId: {
                                                type: "string",
                                                description: "Animal ID or MAC address to filter events",
                                        },
                                        startDate: {
                                                type: "string",
                                                description: "Start date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        endDate: {
                                                type: "string",
                                                description: "End date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        limit: {
                                                type: "number",
                                                description: "Number of records to return (1-50, default: 10)",
                                                minimum: 1,
                                                maximum: 50,
                                        },
                                },
                        },
                },
                {
                        name: TOOL_IDS.ANIMAL_DETAILS,
                        description: "Retrieves detailed information about a specific animal including health score, group, location, and sensor data.",
                        parameters: {
                                type: "object",
                                properties: {
                                        animalId: {
                                                type: "string",
                                                description: "Animal ID or MAC address",
                                        },
                                },
                                required: ["animalId"],
                        },
                },
                {
                        name: TOOL_IDS.CALF_LIFECYCLE_OVERVIEW,
                        description: "Retrieves lifecycle stage distribution showing how many calves are in each stage (newborn, pre-weaning, weaning, post-weaning, grower, finisher, sold, deceased).",
                        parameters: {
                                type: "object",
                                properties: {},
                        },
                },
                {
                        name: TOOL_IDS.VACCINATION_STATUS,
                        description: "Retrieves vaccination compliance data including overdue vaccinations, upcoming due dates, and overall compliance percentage.",
                        parameters: {
                                type: "object",
                                properties: {
                                        animalId: {
                                                type: "string",
                                                description: "Optional animal ID or MAC address to filter for a specific calf",
                                        },
                                        days: {
                                                type: "number",
                                                description: "Look-ahead window in days for upcoming vaccinations (default: 7)",
                                                minimum: 1,
                                                maximum: 90,
                                        },
                                },
                        },
                },
                {
                        name: TOOL_IDS.WEIGHT_GROWTH_ANALYSIS,
                        description: "Retrieves weight records and calculates Average Daily Gain (ADG) for a specific calf or herd summary. Detects weight anomalies.",
                        parameters: {
                                type: "object",
                                properties: {
                                        animalId: {
                                                type: "string",
                                                description: "Animal ID or MAC address. If omitted, returns herd-level summary.",
                                        },
                                        limit: {
                                                type: "number",
                                                description: "Number of weight records to return (1-50, default: 10)",
                                                minimum: 1,
                                                maximum: 50,
                                        },
                                },
                        },
                },
                {
                        name: TOOL_IDS.DISEASE_OVERVIEW,
                        description: "Retrieves disease records including scours/BRD rates, Wisconsin score averages, and active disease cases. Provides disease incidence overview for the herd.",
                        parameters: {
                                type: "object",
                                properties: {
                                        animalId: {
                                                type: "string",
                                                description: "Optional animal ID or MAC to filter for a specific calf",
                                        },
                                        category: {
                                                type: "string",
                                                enum: ["respiratory", "digestive", "navel", "other"],
                                                description: "Optional disease category filter",
                                        },
                                },
                        },
                },
                {
                        name: TOOL_IDS.CALF_HEALTH_KPIS,
                        description: "Retrieves all 8 calf health KPI values: mortality rate, average ADG, vaccination compliance, disease incidence, scours rate, BRD rate, average Wisconsin score, and total animals.",
                        parameters: {
                                type: "object",
                                properties: {},
                        },
                },
                {
                        name: TOOL_IDS.WORK_PLANNER_OVERVIEW,
                        description: "Retrieves work planner tasks. Can filter by status, type, date range, assigned worker, or device.",
                        parameters: {
                                type: "object",
                                properties: {
                                        status: {
                                                type: "string",
                                                enum: ["pending", "in-progress", "completed", "cancelled"],
                                                description: "Filter by task status",
                                        },
                                        type: {
                                                type: "string",
                                                enum: ["Work", "Health", "Treatment", "General"],
                                                description: "Filter by task type",
                                        },
                                        startDate: {
                                                type: "string",
                                                description: "Start date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        endDate: {
                                                type: "string",
                                                description: "End date for date range filter in ISO format (YYYY-MM-DD)",
                                        },
                                        assignedWorkerId: {
                                                type: "string",
                                                description: "Worker ID, token, or name to filter tasks",
                                        },
                                        limit: {
                                                type: "number",
                                                description: "Number of tasks to return (1-20, default: 5)",
                                                minimum: 1,
                                                maximum: 20,
                                        },
                                },
                        },
                },
                {
                        name: TOOL_IDS.WORK_PLANNER_WORKERS,
                        description: "Retrieves list of workers/employees in the work planner system.",
                        parameters: {
                                type: "object",
                                properties: {},
                        },
                },
                {
                        name: TOOL_IDS.WORK_PLANNER_CREATE_TASK,
                        description: "Creates a new task in the work planner. Requires title and start date.",
                        parameters: {
                                type: "object",
                                properties: {
                                        title: {
                                                type: "string",
                                                description: "Task title or name",
                                        },
                                        startDate: {
                                                type: "string",
                                                description: "Start date and time in ISO format (YYYY-MM-DDTHH:mm:ssZ) or date (YYYY-MM-DD)",
                                        },
                                        endDate: {
                                                type: "string",
                                                description: "End date and time in ISO format (optional)",
                                        },
                                        durationMinutes: {
                                                type: "number",
                                                description: "Task duration in minutes (default: 60)",
                                        },
                                        status: {
                                                type: "string",
                                                enum: ["pending", "in-progress", "completed", "cancelled"],
                                                description: "Task status (default: pending)",
                                        },
                                        type: {
                                                type: "string",
                                                enum: ["Work", "Health", "Treatment", "General"],
                                                description: "Task type (default: Work)",
                                        },
                                        notes: {
                                                type: "string",
                                                description: "Task notes or description",
                                        },
                                        location: {
                                                type: "string",
                                                description: "Task location",
                                        },
                                        assignedWorkerId: {
                                                type: "string",
                                                description: "Worker ID, token, or name to assign task to",
                                        },
                                },
                                required: ["title", "startDate"],
                        },
                },
                {
                        name: TOOL_IDS.BIONODE_STATUS,
                        description: "Retrieves status information for all BioNodes (sensor network devices).",
                        parameters: {
                                type: "object",
                                properties: {},
                        },
                },
                {
                        name: TOOL_IDS.BIONODE_ANALYSIS,
                        description: "Retrieves detailed analysis and diagnostics for BioNodes including connectivity, health, and sensor registration status.",
                        parameters: {
                                type: "object",
                                properties: {},
                        },
                },
                {
                        name: TOOL_IDS.BILLING_STATUS,
                        description: "Retrieves user billing status and subscription information.",
                        parameters: {
                                type: "object",
                                properties: {},
                        },
                },
                {
                        name: TOOL_IDS.NOTIFICATION_PREFERENCES,
                        description: "Retrieves user notification preferences and settings.",
                        parameters: {
                                type: "object",
                                properties: {},
                        },
                },
                {
                        name: TOOL_IDS.NAVIGATE_PAGE,
                        description: "Navigates to a specific page in the application. This is a UI action, not a data retrieval tool.",
                        parameters: {
                                type: "object",
                                properties: {
                                        path: {
                                                type: "string",
                                                description: "Page path to navigate to (e.g., '/animals', '/dashboard')",
                                        },
                                },
                                required: ["path"],
                        },
                },
                {
                        name: TOOL_IDS.NAVIGATE_ANIMAL,
                        description: "Navigates to a specific animal's detail page. This is a UI action, not a data retrieval tool.",
                        parameters: {
                                type: "object",
                                properties: {
                                        animalId: {
                                                type: "string",
                                                description: "Animal ID or MAC address",
                                        },
                                },
                                required: ["animalId"],
                        },
                },
                // New diagnostic and analysis tools
                {
                        name: TOOL_IDS.HEALTH_DIAGNOSIS_ASSIST,
                        description: "Analyzes an animal's health data to suggest potential issues based on symptoms, activity patterns, treatment history, and sensor data. Provides differential diagnosis suggestions, urgency assessment, and recommended next steps. Use this for complex health assessments when a user reports symptoms or asks about an animal's health status.",
                        parameters: {
                                type: "object",
                                properties: {
                                        animalId: {
                                                type: "string",
                                                description: "Animal ID or MAC address to analyze",
                                        },
                                        symptoms: {
                                                type: "array",
                                                items: { type: "string" },
                                                description: "Optional list of observed symptoms reported by the user (e.g., 'coughing', 'not eating', 'limping')",
                                        },
                                        includeHistory: {
                                                type: "boolean",
                                                description: "Include treatment and event history in analysis (default: true)",
                                        },
                                },
                                required: ["animalId"],
                        },
                },
                {
                        name: TOOL_IDS.COMPARATIVE_ANALYSIS,
                        description: "Compares performance metrics between groups, time periods, or individual animals. Useful for benchmarking, identifying patterns, and evaluating performance differences. Use this when users ask to compare groups, analyze trends between periods, or benchmark performance.",
                        parameters: {
                                type: "object",
                                properties: {
                                        compareType: {
                                                type: "string",
                                                enum: ["groups", "periods", "animals"],
                                                description: "Type of comparison: 'groups' compares animal groups, 'periods' compares time periods, 'animals' compares individual animals",
                                        },
                                        subjects: {
                                                type: "array",
                                                items: { type: "string" },
                                                description: "Items to compare. For groups: group names (e.g., ['pre-weaning', 'post-weaning', 'grower']). For periods: period labels (e.g., ['last-month', 'this-month'] or dates). For animals: animal IDs.",
                                        },
                                        metrics: {
                                                type: "array",
                                                items: { type: "string" },
                                                description: "Metrics to compare (e.g., ['healthScore', 'treatmentCount', 'birthRate']). If not specified, uses default metrics for the comparison type.",
                                        },
                                },
                                required: ["compareType", "subjects"],
                        },
                },
                {
                        name: TOOL_IDS.GENERATE_REPORT,
                        description: "Generates a formatted summary report for a specific topic or time period. Use this for weekly reviews, health overviews, breeding status summaries, or preparing for veterinary visits.",
                        parameters: {
                                type: "object",
                                properties: {
                                        reportType: {
                                                type: "string",
                                                enum: ["weekly-summary", "health-overview", "treatment-summary", "vet-prep"],
                                                description: "Type of report: 'weekly-summary' for overall performance, 'health-overview' for health metrics, 'treatment-summary' for treatment activity, 'vet-prep' for veterinary visit preparation",
                                        },
                                        startDate: {
                                                type: "string",
                                                description: "Start date for report period in ISO format (YYYY-MM-DD). Defaults to 7 days ago for weekly, 30 days for monthly reports.",
                                        },
                                        endDate: {
                                                type: "string",
                                                description: "End date for report period in ISO format (YYYY-MM-DD). Defaults to today.",
                                        },
                                        focusGroup: {
                                                type: "string",
                                                description: "Optional group name to focus the report on (e.g., 'pre-weaning', 'post-weaning', 'grower')",
                                        },
                                },
                                required: ["reportType"],
                        },
                },
                {
                        name: TOOL_IDS.ACTIVITY_ANOMALY_ANALYSIS,
                        description: "Analyzes activity pattern anomalies across the herd or specific groups. Identifies animals with unusual behavior changes that may indicate health issues, heat cycles, or environmental problems. Use this when investigating health concerns, checking for disease outbreaks, or monitoring group behavior.",
                        parameters: {
                                type: "object",
                                properties: {
                                        timeframe: {
                                                type: "string",
                                                enum: ["24h", "48h", "7d"],
                                                description: "Timeframe to analyze activity patterns (default: '48h')",
                                        },
                                        group: {
                                                type: "string",
                                                description: "Optional group name to focus analysis on",
                                        },
                                        anomalyType: {
                                                type: "string",
                                                enum: ["all", "decreased", "increased", "pattern-change"],
                                                description: "Type of anomaly to look for: 'decreased' for reduced activity, 'increased' for elevated activity, 'pattern-change' for unusual patterns, 'all' for any anomaly (default: 'all')",
                                        },
                                        threshold: {
                                                type: "number",
                                                description: "Percentage deviation from baseline to flag as anomaly (default: 30)",
                                                minimum: 10,
                                                maximum: 80,
                                        },
                                },
                        },
                },
                {
                        name: TOOL_IDS.FEEDING_ANALYSIS,
                        description: "Analyzes water consumption and feeding patterns from BioFlow sensors. Identifies animals or groups with abnormal consumption that may indicate health issues, heat stress, or equipment problems. Use this when investigating feed intake concerns or monitoring water consumption.",
                        parameters: {
                                type: "object",
                                properties: {
                                        sensorId: {
                                                type: "string",
                                                description: "Specific BioFlow sensor ID to analyze. If not provided, analyzes all available sensors.",
                                        },
                                        timeframe: {
                                                type: "string",
                                                enum: ["today", "yesterday", "7d", "30d"],
                                                description: "Timeframe for analysis (default: '7d')",
                                        },
                                        alertThreshold: {
                                                type: "number",
                                                description: "Percentage deviation from baseline to flag as anomaly (default: 30)",
                                                minimum: 10,
                                                maximum: 80,
                                        },
                                        group: {
                                                type: "string",
                                                description: "Optional group name to focus analysis on",
                                        },
                                },
                        },
                },
        ];
}

